/** Small pieces used across the site: messages that pop up, buttons that wait, loading and errors */
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';

/* Messages in the corner */

interface Toast {
	id: number;
	text: string;
	kind: 'good' | 'bad' | '';
}
let toasts: Toast[] = [];
let nextToast = 1;
const listeners = new Set<(toasts: Toast[]) => void>();

export function toast(text: string, kind: Toast['kind'] = '') {
	const id = nextToast++;
	toasts = [...toasts, { id, text, kind }];
	for (const listener of listeners) listener(toasts);
	setTimeout(() => {
		toasts = toasts.filter(t => t.id !== id);
		for (const listener of listeners) listener(toasts);
	}, kind === 'bad' ? 9000 : 5000);
}

export function Toasts() {
	const [list, setList] = useState(toasts);
	useEffect(() => {
		listeners.add(setList);
		return () => {
			listeners.delete(setList);
		};
	}, []);
	return <div class="toasts" role="status" aria-live="polite">
		{list.map(t => <div key={t.id} class={`toast ${t.kind}`}>{t.text}</div>)}
	</div>;
}

/* Buttons */

interface ActionButtonProps {
	/** Runs when pressed; the button waits for it */
	action: () => Promise<unknown>;
	children: ComponentChildren;
	class?: string;
	disabled?: boolean;
	/** Asks before doing it */
	confirm?: string;
	title?: string;
}

export function ActionButton({ action, children, class: className, disabled, confirm, title }: ActionButtonProps) {
	const [busy, setBusy] = useState(false);
	return <button type="button" class={className} disabled={disabled || busy} title={title} onClick={async () => {
		if (confirm && !window.confirm(confirm)) return;
		setBusy(true);
		try {
			await action();
		} finally {
			setBusy(false);
		}
	}}>{busy ? '…' : children}</button>;
}

/* Loading */

export function Loading({ text = 'Loading…' }: { text?: string }) {
	return <p class="muted">{text}</p>;
}

export function ErrorBox({ error }: { error: unknown }) {
	return <p class="notice bad">{error instanceof Error ? error.message : String(error)}</p>;
}

/** Time since a date, roughly: "3 minutes ago" */
export function ago(date: string | number | Date) {
	const seconds = Math.max(0, (Date.now() - new Date(date).getTime()) / 1000);
	const units: [number, string][] = [[86400 * 365, 'year'], [86400 * 30, 'month'], [86400, 'day'], [3600, 'hour'], [60, 'minute']];
	for (const [size, unit] of units) {
		const n = Math.floor(seconds / size);
		if (n >= 1) return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
	}
	return 'just now';
}

/** A countdown to a time, updated every second: "4:59" */
export function Countdown({ until }: { until: number }) {
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, []);
	const left = Math.max(0, Math.round((until - now) / 1000));
	const hours = Math.floor(left / 3600);
	const minutes = Math.floor((left % 3600) / 60);
	const seconds = `${left % 60}`.padStart(2, '0');
	return <span class="nowrap">{hours ? `${hours}:${`${minutes}`.padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`}</span>;
}
